// ============================================================================
// ORDERS · Collection — the Wall's control deck, as pure facts (C8 Commit 3)
// ============================================================================
// The approved prototype (docs/prototypes/2026-09-24-collection-wall-v3-shape-
// deck-proto-v3.html) decides the look; David's decisions decide the data:
//   D-C8-3 · material = the order's (the read already puts the Book first);
//            case is normalised for display only; FAMILY: Metal = Metal ·
//            Aluminium · Metal - Nickel free · Titanium · Frameless, Acetate =
//            Acetate · TR · PC · Ultem · Injection · TPE (David 09.10: TPE is a
//            plastic). Anything else is in neither.
//   D-C8-4 · shape = the order's shape + sub-shape; display-only fixes
//            wrap → Wrap · Double bridge → D.bridge · Cateye → Cat.
//   D-C8-6 · "Not ordered yet" = Awaiting new cards + Planned-only repeats.
// One ROW per Wall item (a new card or a repeat) carries the facts every
// facet reads, so the counts and the filter can never use two rules. Every
// count ignores its own facet and applies all the others (the prototype's
// base(r, skip)). Picks are multi-select inside a facet and AND across them.
// The URL codec lives here too: &fam= · &mat= · &sub= (parent|sub, comma-
// separated) · &nyo=1. Pure — tested in collectionFacets.test.js.
// ============================================================================
import { displayMaterial } from './collectionModel';

export const FAMILIES = [['metal', 'Metal'], ['acetate', 'Acetate']];
export const FAMILY_KEYS = FAMILIES.map(([k]) => k);
export const FAMILY_MATERIALS = {
  metal: ['Metal', 'Aluminium', 'Metal - Nickel free', 'Titanium', 'Frameless'],
  acetate: ['Acetate', 'TR', 'PC', 'Ultem', 'Injection', 'TPE'],   // the plastics — TPE added 09.10 (David)
};
const ALL_FAMILY_MATERIALS = [...FAMILY_MATERIALS.metal, ...FAMILY_MATERIALS.acetate];

const norm = (v) => String(v == null ? '' : v).trim().replace(/\s+/g, ' ').toLowerCase();
const num = (v) => (v == null || v === '' || Number.isNaN(Number(v)) ? null : Number(v));
const CANON = new Map();
Object.entries(FAMILY_MATERIALS).forEach(([fam, list]) => list.forEach((label) => CANON.set(norm(label), [fam, label])));

/** { family: 'metal' | 'acetate' | null, label } — the family list's own spelling when the value is on
 *  one of the two lists (METAL → Metal, ACETATE → Acetate, INJECTION → Injection), else the
 *  display form (collectionModel.displayMaterial) and no family. A blank material is { null, null }. */
export function materialOf(raw) {
  const t = norm(raw);
  if (!t) return { family: null, label: null };
  const hit = CANON.get(t);
  return hit ? { family: hit[0], label: hit[1] } : { family: null, label: displayMaterial(String(raw).trim()) };
}

const SHAPE_FIX = { wrap: 'Wrap', 'double bridge': 'D.bridge', cateye: 'Cat' };
/** The shape word for display (D-C8-4); the data is never touched. */
export function shapeLabel(raw) {
  const t = String(raw == null ? '' : raw).trim();
  if (!t) return null;
  return SHAPE_FIX[norm(t)] || t;
}
/** { parent, sub, key: 'parent|sub' } — a missing sub reads as its parent; no shape → null. */
export function shapeOf(item) {
  const p = shapeLabel(item?.shape), s = shapeLabel(item?.sub_shape);
  const parent = p || s, sub = s || p;
  return parent ? { parent, sub, key: `${parent}|${sub}` } : null;
}

/* ------------------------------------------------------------- rows */
/** D-C8-6: only an Awaiting new card and a Planned-only repeat are "not ordered yet". */
export const isOrderedItem = (kind, item) => (kind === 'rep' ? item?.status !== 'planned' : item?.status !== 'awaiting');

/** One Wall item as the deck reads it. Pieces and Colours are the order's (the Book): a new card's
 *  qty / colours, a repeat's ordered_qty / ordered_colours (C8 §4 — never the email's colour count). */
export function wallRow(kind, item) {
  const m = materialOf(item?.material);
  return {
    kind, item,
    code: kind === 'rep' ? item?.model : item?.code,
    ordered: isOrderedItem(kind, item),
    family: m.family, material: m.label,
    shape: shapeOf(item),
    pieces: num(kind === 'rep' ? item?.ordered_qty : item?.qty),
    colours: num(kind === 'rep' ? item?.ordered_colours : item?.colours),
  };
}
/** The line's Wall in one list — repeats first, then the new cards (D-C8-5's order). */
export const wallRows = (cards, repeats) => [...(repeats || []).map((r) => wallRow('rep', r)), ...(cards || []).map((c) => wallRow('new', c))];
/** A line's Wall total — ordered models only (the Wall tab, D-C8-6); + the not-ordered ones while the
 *  toggle is on (the line switch's count, the prototype's renderLine). */
export const wallTotal = (cards, repeats, nyo = false) =>
  (cards || []).filter((c) => nyo || isOrderedItem('new', c)).length + (repeats || []).filter((r) => nyo || isOrderedItem('rep', r)).length;

/** A line's Wall total as the line switch shows it — never a 0 before both reads answer.
 *  `cr` / `rr` are the cards' and the repeats' read states ({ state, cards | repeats }). Home's tile reads it too. */
export const lineTotal = (cr, rr, nyo) => {
  const state = cr.state === 'failed' || rr.state === 'failed' ? 'failed' : cr.state === 'ready' && rr.state === 'ready' ? 'ready' : 'loading';
  return { state, n: state === 'ready' ? wallTotal(cr.cards, rr.repeats, nyo) : null };
};

/* ------------------------------------------------------------- picks */
export const EMPTY_PICKS = { kind: 'all', fam: '', mats: [], subs: [], nyo: false };

/** Does a row pass the picks? `skip` names the ONE facet a count is taken for ('kind' · 'fam' ·
 *  'mat' · 'sub'); 'fam' also skips the materials, which live inside a family (the prototype's rule). */
export function passes(row, picks, skip = null) {
  const p = picks || EMPTY_PICKS;
  if (!p.nyo && !row.ordered) return false;
  if (skip !== 'kind' && p.kind === 'rep' && row.kind !== 'rep') return false;
  if (skip !== 'kind' && p.kind === 'new' && row.kind !== 'new') return false;
  if (skip !== 'fam' && p.fam && row.family !== p.fam) return false;
  if (skip !== 'fam' && skip !== 'mat' && p.mats?.length && !p.mats.includes(row.material)) return false;
  if (skip !== 'sub' && p.subs?.length && !(row.shape && p.subs.includes(row.shape.key))) return false;
  return true;
}
export const hasPicks = (p) => !!(p && (p.fam || p.mats?.length || p.subs?.length));

/** parent → sub-shape list over the line's rows (every row, ordered or not): parents by volume, the
 *  parent-named sub first, then by volume. A parent whose only sub is itself is a plain chip;
 *  anything else (Fem · Men · Trendy today) is a capsule. */
export function taxonomy(rows) {
  const par = new Map();
  (rows || []).forEach((r) => {
    if (!r.shape) return;
    if (!par.has(r.shape.parent)) par.set(r.shape.parent, new Map());
    const m = par.get(r.shape.parent);
    m.set(r.shape.sub, (m.get(r.shape.sub) || 0) + 1);
  });
  return [...par.entries()].map(([parent, m]) => ({
    parent,
    n: [...m.values()].reduce((a, b) => a + b, 0),
    capsule: !(m.size === 1 && m.has(parent)),
    subs: [...m.entries()].sort((a, b) => (b[0] === parent) - (a[0] === parent) || b[1] - a[1] || a[0].localeCompare(b[0]))
      .map(([sub]) => ({ sub, key: `${parent}|${sub}` })),
  })).sort((a, b) => b.n - a.n || a.parent.localeCompare(b.parent));
}

/** Every number the deck prints. `all` = the line's rows untouched (the total, the toggle's count, the
 *  tray's materials); `rows` = after the search and the popover's facets (every facet count). */
export function deckCounts(all, rows, picks) {
  const p = picks || EMPTY_PICKS;
  const tally = (list, keyOf) => { const m = new Map(); list.forEach((r) => { const k = keyOf(r); if (k != null) m.set(k, (m.get(k) || 0) + 1); }); return m; };
  const kindBase = rows.filter((r) => passes(r, p, 'kind'));
  const famBase = rows.filter((r) => passes(r, p, 'fam'));
  const shown = rows.filter((r) => passes(r, p));
  const inLine = (all || []).filter((r) => r.ordered || p.nyo);
  const present = new Set(inLine.map((r) => r.material).filter(Boolean));
  return {
    kinds: { all: kindBase.length, rep: kindBase.filter((r) => r.kind === 'rep').length, new: kindBase.filter((r) => r.kind === 'new').length },
    fams: { all: famBase.length, ...Object.fromEntries(FAMILY_KEYS.map((k) => [k, famBase.filter((r) => r.family === k).length])) },
    mats: tally(rows.filter((r) => passes(r, p, 'mat')), (r) => r.material),
    tray: p.fam ? FAMILY_MATERIALS[p.fam].filter((m) => present.has(m)) : [],
    subs: tally(rows.filter((r) => passes(r, p, 'sub')), (r) => (r.shape ? r.shape.key : null)),
    shown: shown.length,
    pieces: shown.reduce((a, r) => a + (r.pieces || 0), 0),
    total: inLine.length,
    notOrdered: (all || []).filter((r) => !r.ordered).length,
  };
}

/** The values outside both families, as met in the rows (the report's list) — blank excluded. */
export const unplacedMaterials = (rows) => [...new Set((rows || []).filter((r) => r.material && !r.family).map((r) => r.material))].sort();

/* ------------------------------------------------------------- picks: clean + the address */
const SUB_RE = /^[^|,]{1,60}\|[^|,]{1,60}$/;
const uniq = (list) => list.filter((v, i, a) => a.indexOf(v) === i);
/** The store's guard: fam ∈ metal · acetate (else none), materials only from the picked family's list,
 *  sub keys only in the `parent|sub` shape. */
export function cleanPicks({ fam, mats, subs } = {}) {
  const f = FAMILY_KEYS.includes(fam) ? fam : '';
  const allowed = f ? FAMILY_MATERIALS[f] : [];
  return {
    fam: f,
    mats: uniq((Array.isArray(mats) ? mats : []).map(String).filter((m) => allowed.includes(m))),
    subs: uniq((Array.isArray(subs) ? subs : []).map((v) => String(v).trim()).filter((v) => SUB_RE.test(v))),
  };
}
/** Store → address: absent when default. */
export function encodePicks(c) {
  return {
    fam: c?.fam || null,
    mat: c?.mats?.length ? c.mats.join(',') : null,
    sub: c?.subs?.length ? c.subs.join(',') : null,
    nyo: c?.nyo ? '1' : null,
  };
}
/** Address → store patch (only the keys present). `get` is URLSearchParams#get. */
export function decodePicks(get) {
  const out = {};
  const split = (v) => String(v || '').split(',').map((x) => x.trim()).filter(Boolean);
  const fam = get('fam');
  if (fam) {
    const c = cleanPicks({ fam, mats: split(get('mat')) });
    if (c.fam) { out.fam = c.fam; if (c.mats.length) out.mats = c.mats; }
  }
  const subs = cleanPicks({ subs: split(get('sub')) }).subs;
  if (subs.length) out.subs = subs;
  if (get('nyo') === '1') out.nyo = true;
  return out;
}
export { ALL_FAMILY_MATERIALS };
